import {useCallback} from 'react';

import {FetchBaseQueryError} from '@reduxjs/toolkit/query';

import {sleep} from '@/app/utils/sleep';
import {C_ORIGIN} from '@/constants/OriginConstants';
import {useUsersRepository} from '@/dataSource/src/api/users/useRepository';
import {useAuthenticationToken} from '@/services/authentication/AuthenticationService';
import logError from '@/utils/errors/logError';

import {useTrackers as useVerifySmsTrackers} from '../pages/VerifySMS/trackers';

type VerifyAuthProcessOptions = {
  referralCode?: string;
  invitationCode?: string;
  flow?: typeof C_ORIGIN.SIGNUP | typeof C_ORIGIN.SIGNIN;
  phone?: string;
  email?: string;
};

// Delay (ms) before fetching the just-authenticated user's profile, and
// again before the single retry on 401. Newly-created accounts can hit a
// short window where the token is valid but the backend hasn't finished
// provisioning the profile record yet (seen on sign-up), so we give the
// backend a moment before asking for the profile — and one more moment if
// that first attempt still comes back 401.
//
// Retry kept to one on purpose: every attempt here already goes through
// reAuth's own internal 401 handling, which itself forces up to 2 Cognito
// session refreshes (fetchAuthSession({forceRefresh: true})) before giving
// up on that attempt. Stacking more retries on top multiplies that into a
// refresh burst — if Cognito ever rate-limits or hiccups on one of those
// forced refreshes, reAuth's refreshUserSession logs the user out.
const PROFILE_FETCH_DELAY_MS = 1000;

const isUnauthorizedError = (error: unknown): boolean =>
  (error as FetchBaseQueryError)?.status === 401;

/**
 * Persists the authenticated session (redux token slice + Sentry user) and
 * identifies the user for analytics (mixpanel.identify via trackVerifyToken).
 *
 * This must run on every path that ends a sign-in/sign-up flow with a valid
 * access token — including flows that skip the OTP screen entirely (e.g. AWS
 * Cognito returning the DONE challenge directly from `signIn`) — otherwise
 * the session keeps tracking under an anonymous device id that never gets
 * merged with the user's identity in the analytics tool.
 */
export const useVerifyAuthProcess = () => {
  const {triggerFetchProfileQuery} = useUsersRepository();
  const {manageDataToken} = useAuthenticationToken();
  const {trackVerifyToken} = useVerifySmsTrackers();

  // Fetches the just-authenticated user's profile, waiting a bit first (and
  // once more on a single retry) to ride out the brief window (post sign-up)
  // where the token is valid but the backend hasn't finished provisioning
  // the profile record yet. manageDataToken dispatches setTokenData
  // synchronously, so the token itself is already in the Redux store by the
  // time this runs — the delay here is purely for the backend, not for
  // Redux propagation.
  const fetchProfileWithDelay = useCallback(async () => {
    await sleep(PROFILE_FETCH_DELAY_MS);
    try {
      return await triggerFetchProfileQuery().unwrap();
    } catch (error) {
      if (!isUnauthorizedError(error)) throw error;
      await sleep(PROFILE_FETCH_DELAY_MS);
      return triggerFetchProfileQuery().unwrap();
    }
  }, [triggerFetchProfileQuery]);

  // Identifies the just-authenticated user for analytics (mixpanel.identify
  // via trackVerifyToken) — kept as its own function so this call has its
  // own try/catch and doesn't rely on the outer one, which has already
  // returned by the time this runs.
  const trackVerifiedUserProfile = useCallback(
    async (accessToken: string, options: VerifyAuthProcessOptions) => {
      const {referralCode, invitationCode, flow, phone, email} = options;
      try {
        const user = await fetchProfileWithDelay();
        trackVerifyToken(
          accessToken,
          user?.referral_code?.code,
          referralCode,
          invitationCode,
        );
      } catch (error) {
        logError(error, {
          fileName: 'useVerifyAuthProcess',
          functionName: 'trackVerifiedUserProfile',
          extraTags: {
            flow,
            ...(phone && {'user.phone': phone}),
            ...(email && {'user.email': email}),
          },
        });
      }
    },
    [trackVerifyToken, fetchProfileWithDelay],
  );

  const verifyAuthProcess = useCallback(
    async (accessToken: string, options: VerifyAuthProcessOptions = {}) => {
      const {
        referralCode,
        invitationCode,
        flow = C_ORIGIN.SIGNIN,
        phone,
        email,
      } = options;
      try {
        manageDataToken({accessToken});
        trackVerifiedUserProfile(accessToken, {
          referralCode,
          invitationCode,
          flow,
          phone,
          email,
        });
      } catch (error) {
        logError(error, {
          fileName: 'useVerifyAuthProcess',
          functionName: 'verifyAuthProcess',
          extraTags: {
            flow,
            ...(phone && {'user.phone': phone}),
            ...(email && {'user.email': email}),
          },
        });
      }
    },
    [manageDataToken, trackVerifiedUserProfile],
  );

  return {verifyAuthProcess};
};
